import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {merchantService} from '@/services/merchants';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

// Minimal business fetch so the webhooks page can identify which business's
// deliveries are being viewed — otherwise the page is just a bare list with
// no indication of whose webhooks these are.
export default function useBusinessSummary(businessId: string) {
  const [business, setBusiness] = useState<AfriexBusiness | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBusiness = useCallback(
    async (signal?: AbortSignal) => {
      if (!businessId) {
        return;
      }
      try {
        setIsLoading(true);
        const response = await merchantService.getBusinessDetails(
          businessId,
          signal,
        );
        setBusiness(response.business);
      } catch (err) {
        if (signal?.aborted) {
          return;
        }
        const errorMessage = getErrorMessage(err, '');
        toast.error('Failed to fetch business details: ' + errorMessage);
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      }
    },
    [businessId],
  );

  useEffect(() => {
    const controller = new AbortController();
    // `fetchBusiness` toasts its own failures, so this only fires if that catch
    // or the finally itself throws. Log rather than swallow silently.
    fetchBusiness(controller.signal).catch(
      logRejection('Failed to fetch business summary'),
    );
    return () => controller.abort();
  }, [fetchBusiness]);

  return {business, isLoading};
}
